/**
 * 複数の商品をグループ化するための処理
 */
import _ from 'lodash';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { IArrival } from '@/coreDomainsModule/entities/arrival';
import { IAnnounce } from '@/coreDomainsModule/entities/forecast';
import { StdInventoryIndex } from '@/coreDomainsModule/entities/inventory';
import { setBusinessId } from '@/businessRepository/idUtils';
import { getBusinessManager } from '@/businessRepository/organizationTable/businessManage';
import { simpleCopy } from '@/utils/deepCopy';
import { ISalesResult } from '../interfaces/salesResults';
import { TranslateExcelBusiness } from '../../coreDomainsModule/translateExcel';
import { IYearMonth, equalYearMonthInfo } from '../interfaces/date';
import { defaultAnnounce, getInitialData } from '../importData/initialData';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { FORECAST_INITIAL_VALUE, FORECAST_LENGTH } from '@/coreDomainsModule/constants';

const checkList = [
  'organizationId',
  'businessName',
  'supplierName',
  'deliveryDestinationName',
  'salesDestinationName',
  'businessName',
  'endUser',
  'unit',
  'form',
  'MOQ',
  'note',
  'leadTime',
];

const getDateValue = (a: IArrival): number => a.year * 10000 + a.month * 100 + a.day;

/**
 * 与えられたarrayの中身が全部同じかどうかをチェックする。
 * 異なるものが入っていた場合はnullを返す。
 * @param values
 */
const checkIdentity = (values: any[]): any => {
  const check = new Set<any>(values);
  if (check.size > 1) return null;
  return [...check][0];
};

/**
 *
 * @param targetBusinesses
 */
const validateGrouping = (
  targetBusinesses: IBusiness[],
  validateMap: { [key: string]: any }
): { status: boolean; fails: { [key: string]: boolean } | undefined } => {
  const fails = {};
  for (let i = 1; i < targetBusinesses.length; i++) {
    const p = targetBusinesses[i];
    for (const key of Object.keys(p)) {
      if (validateMap.hasOwnProperty(key)) {
        // validateがある場合
        if (p[key] !== targetBusinesses[0][key]) {
          // 全てのbusinessesに対してbusinesses[0]と対象のvalueが等価か調べる
          fails[key] = true;
        }
      }
    }
  }
  if (Object.keys(fails).length == 0) {
    return { status: true, fails: undefined };
  } else {
    return { status: false, fails: fails };
  }
};

/**
 *
 * @param groupBusiness
 */
const registerGroup = async (groupBusiness: IBusiness): Promise<boolean> => {
  if (!groupBusiness.group) return false;
  const res = await getBusinessManager().register(groupBusiness);
  if (!res) return false;
  await getBusinessManager().remove(groupBusiness.group);
  // TODO: 失敗したときにロールバックする必要がある（トランザクション処理）
  return true;
};

/**
 *
 * @param groupBusiness
 */
const ungroup = async (groupBusiness: IBusiness): Promise<boolean> => {
  if (!groupBusiness.group) return false;
  for await (const p of getBusinessManager().getBusinessesInGroup(groupBusiness)) {
    const res = await getBusinessManager().register(p);
    if (!res) return false;
  } // TODO: 途中で1つ失敗したときなど、ロールバックする（トランザクション処理）
  await getBusinessManager().remove([groupBusiness.businessId as string]);
  return true;
};

/**
 * mvp用
 * 集計行取引に関連付いた取引情報をグループ化する関数
 * @param relatedBusinesses // 関連取引
 * @param aggregateBusiness // 集計行取引
 */
const makeAggregateGroup = async (
  relatedBusinesses: IBusiness[],
  aggregateBusiness: TranslateExcelBusiness
): Promise<TranslateExcelBusiness> => {
  const groupedBusiness: TranslateExcelBusiness = aggregateBusiness
    ? simpleCopy(aggregateBusiness)
    : { ...getInitialData(), translateBusinessId: '', aggregateBusiness: false };
  // 関連取引がない場合、グループ化処理を行わずに取引情報をそのまま返すようにする
  // TODO: これは、translateBusinessIdが一致するものがない場合に起こる(関連取引と判別するためのプロパティが集計行ではない行で欠けてる場合も含む)
  // ↑複合キー作成のプロパティを削ると正確性が落ちるので、nullぽ処理だけでとどめている
  if (relatedBusinesses.length <= 0) {
    groupedBusiness.aggregateGroup = [];
    return groupedBusiness;
  }
  // 関連取引の取引IDを登録
  groupedBusiness.aggregateGroup = relatedBusinesses.map((p: IBusiness) => p.businessId);
  // 年月が0で登録されている情報(filterで弾きたい情報)
  const rejectYearMonthObject: IYearMonth = {
    year: 0,
    month: 0,
  };

  // 内示情報の取得 (ここで、年月が0の情報を取り除く)
  const aggregateAnnounce: IAnnounce[] = simpleCopy(aggregateBusiness.customerAnnounce ?? []).filter(
    (item) => !equalYearMonthInfo(item, rejectYearMonthObject)
  );

  // 内示数量の集計操作を行う
  // 集計行の内示情報で初期化(ここに数量を足す形で集計)
  // 数量情報だけ0に初期化する
  aggregateAnnounce.map((announce) => {
    if (announce.amount) announce.amount = 0;
  });

  const outputAggregateAnnounce: IAnnounce[] = aggregateAnnounce;
  const initialAnnounce: IAnnounce = {
    amount: FORECAST_INITIAL_VALUE,
    year: 0,
    month: 0,
    manageInitiatedYearMonth: undefined,
    createAt: undefined,
    updateAt: undefined,
  };
  const defaultAnnounce: IAnnounce[] = new Array(FORECAST_LENGTH).fill({ ...initialAnnounce });

  for (const businessObj of relatedBusinesses) {
    const announceArr: IAnnounce[] = businessObj.customerAnnounce ?? simpleCopy(defaultAnnounce);
    for (const announceArrElement of announceArr) {
      // 集計行、関連取引それぞれで年月が一致した内示数量を加算する
      const foundIndex = outputAggregateAnnounce.findIndex((announce) => equalYearMonthInfo(announce, announceArrElement));
      if (foundIndex < 0) {
        outputAggregateAnnounce.push(simpleCopy(announceArrElement));
      } else {
        outputAggregateAnnounce[foundIndex].amount += announceArrElement.amount;
        // 内示受領月の登録
        if (announceArrElement.manageInitiatedYearMonth) {
          // 集計行内示に受領月が登録できているかの確認
          if (outputAggregateAnnounce[foundIndex].manageInitiatedYearMonth) {
            // 登録されている受領月と登録しようとしている受領月を比較
            if (
              DateService.compareYearMonths(
                outputAggregateAnnounce[foundIndex].manageInitiatedYearMonth!,
                announceArrElement.manageInitiatedYearMonth
              ) < 0
            ) {
              outputAggregateAnnounce[foundIndex].manageInitiatedYearMonth = announceArrElement.manageInitiatedYearMonth;
            }
            outputAggregateAnnounce[foundIndex].manageInitiatedYearMonth = announceArrElement.manageInitiatedYearMonth;
          } else {
            outputAggregateAnnounce[foundIndex]['manageInitiatedYearMonth'] = announceArrElement.manageInitiatedYearMonth;
          }
        }
      }
    }
  }

  // 内示情報を年月順にソートしておく
  outputAggregateAnnounce.sort(DateService.compareYearMonths);

  // 集計した内示情報で更新を行う
  groupedBusiness.customerAnnounce = outputAggregateAnnounce;

  // 販売実績情報の取得 (ここで、年月が0の情報を取り除く)
  const aggregateSalesResults: ISalesResult[] = simpleCopy(aggregateBusiness.salesResults ?? []).filter(
    (item) => !equalYearMonthInfo(item, rejectYearMonthObject)
  );

  // 販売実績の集計操作を行う
  // 集計行の販売実績情報で初期化(ここに数量を足す形で集計)
  // 数量情報だけ0に初期化する
  aggregateSalesResults.map((salesResult) => {
    if (salesResult.amount) salesResult.amount = 0;
  });

  const outputAggregateSalesResult = aggregateSalesResults;
  for (const businessObj of relatedBusinesses) {
    const salesResultArr: ISalesResult[] = businessObj.salesResults ?? [];
    for (const salesResultArrElement of salesResultArr) {
      // 集計行、関連取引それぞれで年月が一致した内示数量を加算する
      const foundIndex = outputAggregateSalesResult.findIndex((salesResult) => equalYearMonthInfo(salesResult, salesResultArrElement));
      if (foundIndex < 0) {
        outputAggregateSalesResult.push(simpleCopy(salesResultArrElement));
      } else {
        outputAggregateSalesResult[foundIndex].amount += salesResultArrElement.amount;
      }
    }
  }

  // 販売実績情報を年月順にソートしておく
  outputAggregateSalesResult.sort(DateService.compareYearMonths);

  // 集計した販売実績情報で更新を行う
  groupedBusiness.salesResults = outputAggregateSalesResult;

  // arrivalを統合する。同日に入荷するものを合算する必要はない（シミュレーションで同日入荷を扱えるため）
  // TODO: 石川依頼用　なんとかしてくれ
  // let arrivals: Arrivals[] = [];
  // for (let k = 0; k < relatedBusinesses.length; k++) {
  //   arrivals.push(...relatedBusinesses[k].arrivals);
  // }
  // // 年月が0の情報をfilterで除去する
  // arrivals = arrivals.filter((item) => !equalYearMonthInfo(item, rejectYearMonthObject));
  // // 入荷情報を年月順にソートする
  // arrivals = arrivals.sort((a, b) => {
  //   return getDateValue(a) - getDateValue(b);
  // });
  // groupedBusiness.arrival = arrivals;
  return groupedBusiness;
};

export { registerGroup, ungroup, validateGrouping, makeAggregateGroup };
